import { useQuery } from '@tanstack/react-query'
import { useMemo } from 'react'
import { queriesApi } from '../services/queriesService'

/**
 * Hook para obtener datos de login de Contentful
 * Incluye contenido para el proceso de onboarding y autenticación
 *
 * @returns {Object} Objeto con loading, error, datos procesados y funciones utilitarias
 */
export const useContentfulLogin = () => {
	// Query con Tanstack Query
	const {
		data: rawData,
		isLoading,
		error,
		refetch,
		isStale,
		isRefetching
	} = useQuery({
		queryKey: ['contentful', 'login'] as const,
		queryFn: queriesApi.getLogin,
		staleTime: 5 * 24 * 60 * 60 * 1000, // 5 días
		cacheTime: 5 * 24 * 60 * 60 * 1000, // 5 días
		retry: 2,
		retryDelay: attemptIndex => Math.min(1000 * 2 ** attemptIndex, 30000),
		refetchOnWindowFocus: false,
		refetchOnReconnect: true
	})

	// Manejar errores
	if (error) {
		console.error('Error al obtener datos de Login desde Contentful:', error)
	}

	// Procesar contenido principal
	const referenciaContenidoCollection = useMemo(() => {
		if (!rawData || isLoading || error) return []

		try {
			return (
				rawData?.templateContenidoCollection?.items?.[0]?.referenciaContenidoCollection?.items || []
			)
		} catch (transformError) {
			console.error('Error al procesar contenido principal de login:', transformError)
			return []
		}
	}, [rawData, isLoading, error])

	// Procesar secciones
	const sectionsCollection = useMemo(() => {
		if (!rawData || isLoading || error) return []

		try {
			return rawData?.templateContenidoCollection?.items?.[0]?.sectionsCollection?.items || []
		} catch (transformError) {
			console.error('Error al procesar secciones de login:', transformError)
			return []
		}
	}, [rawData, isLoading, error])

	// Información del cache
	const cacheInfo = useMemo(
		() => ({
			isStale,
			isRefetching,
			lastFetch: rawData ? new Date() : null,
			cacheExpiry: new Date(Date.now() + 5 * 24 * 60 * 60 * 1000)
		}),
		[isStale, isRefetching, rawData]
	)

	return {
		// Datos principales (compatibilidad con hook original)
		loading: isLoading,
		referenciaContenidoCollection,
		sectionsCollection,

		// Funcionalidades adicionales
		error,
		refetch,
		isStale,
		isRefetching,
		cacheInfo,
		rawData
	}
}

export default useContentfulLogin
